import { format, formatDistanceToNow } from "date-fns";
import placeholder from "../assets/noimageplaceholder.svg";
import {
    ActiveCart,
    ActiveCustomer,
    ActiveEntityType,
    ActiveOrder,
    ActiveOutlet,
    ActiveProduct,
    ActiveUser,
    LineItem,
    PaymentMethod,
    RefundItem
} from "../build/activeEntityType";
import { DynamicDataKey } from "../build/dynamicData";
import { DynamicField, TimeFormat } from "../build/types";
import { ClockAction } from "../build/constants";
import { processDynamicData } from "../hooks/useDynamicData";

export function getValueFromObject(path: string[], obj: Record<string, any>) {
    return path.reduce((acc, part) => (acc !== null && acc !== undefined ? acc[part] : undefined), obj);
}

export const formatTime = (value: Date | number | string, timeFormat: TimeFormat): string => {
    try {
        if (timeFormat === "relative") {
            return formatDistanceToNow(value, { addSuffix: true });
        }

        return format(value, timeFormat);
    } catch (e) {
        console.error(e);
        return value?.toString();
    }
};

const joinNonEmptyStrings = (values: (string | undefined)[], separator = " ") =>
    values.filter(Boolean).join(separator) || undefined;

export const dynamicDataSpecialCases: Record<
    DynamicDataKey,
    (entity: any, formatCurrency: (amount: number) => string | number) => string | number | undefined | Date
> = {
    [`${ActiveEntityType.ORDER}.source`]: _ => placeholder,
    [`${ActiveEntityType.ORDER}.status`]: (order: ActiveOrder) => order.status,
    //check this... what ??
    [`${ActiveEntityType.PRODUCT}.images`]: (entity: ActiveProduct) => {
        // @ts-ignore
        return Array.isArray(entity?.images) && entity.images.length > 0 ? entity.images[0] : typeof(entity?.image) === "string" ? entity.image : undefined;
    },
    //todo add taxid
    [`${ActiveEntityType.COMPANY}.taxId`]: () => {
        return "taxId";
    },
    [`${ActiveEntityType.PRODUCT}.subtotal`]: (product: ActiveProduct) => {
        if (
            !product ||
            product.price === undefined ||
            product.price === null ||
            product.quantity === undefined ||
            product.quantity === null
        ) {
            return undefined;
        }

        // Handle potential string values becuase it's kind of a mess right now what we are getting
        const numericPrice =
            typeof product.price === "number" ? product.price : parseFloat(String(product.price).replace(/[^0-9.-]+/g, ""));

        const numericQuantity =
            typeof product.quantity === "number"
                ? product.quantity
                : parseFloat(String(product.quantity).replace(/[^0-9.-]+/g, ""));

        // Guard against NaN results
        if (isNaN(numericPrice) || isNaN(numericQuantity)) return undefined;

        return numericPrice * numericQuantity;
    },
    [`${ActiveEntityType.OUTLET}.fullAddress`]: (outlet: ActiveOutlet) => {
        return joinNonEmptyStrings([outlet?.address, outlet?.city, outlet?.state, outlet?.postCode], ", ");
    },
    [`${ActiveEntityType.ORDER}.paymentMethods`]: (entity: unknown, formatCurrency: (amount: number) => string | number) => {
        const paymentMethods = entity as PaymentMethod[];
        if (!paymentMethods || !Array.isArray(paymentMethods)) return undefined;
        return paymentMethods
            .map(method => {
                const formattedAmount = method?.amount != null ? formatCurrency(Number(method.amount)) : "";
                return `${method?.paymentType}: ${formattedAmount}`;
            })
            .join(", ");
    },

    [`${ActiveEntityType.PRODUCT}.stock`]: (product: ActiveProduct) => {
        if (product?.isUnlimited) return "∞";
        return product?.stock;
    },

    [`${ActiveEntityType.CUSTOMER}.fullName`]: (customer: ActiveCustomer) =>
        joinNonEmptyStrings([customer?.firstName, customer?.lastName]),
    [`${ActiveEntityType.USER}.fullName`]: (user: ActiveUser) => joinNonEmptyStrings([user?.firstName, user?.lastName]),
    time: _ => new Date(),
    date: _ => new Date(),
    [`${ActiveEntityType.CART}.numberOfProductsInCart`]: (cart: ActiveCart) => cart?.products?.length || 0,
    //deprecated:
    [`${ActiveEntityType.PRODUCT}.longDescription`]: (product: ActiveProduct) => product?.description,
    [`${ActiveEntityType.USER}.firstName ${ActiveEntityType.USER}.lastName`]: (user: ActiveUser) =>
        joinNonEmptyStrings([user?.firstName, user?.lastName]),
    [`${ActiveEntityType.OUTLET}.address, ${ActiveEntityType.OUTLET}.city, ${ActiveEntityType.OUTLET}.state, ${ActiveEntityType.OUTLET}.postCode`]:
        (outlet: ActiveOutlet) => joinNonEmptyStrings([outlet?.address, outlet?.city, outlet?.state, outlet?.postCode], ", "),
    [`${ActiveEntityType.CUSTOMER}.firstName ${ActiveEntityType.CUSTOMER}.lastName`]: (customer: ActiveCustomer) =>
        joinNonEmptyStrings([customer?.firstName, customer?.lastName]),
    clockType: entity => {
        if (!entity || !Array.isArray(entity)) return undefined;
        const isClockingIn =
            !!entity &&
            entity.some((type: ClockAction) =>
                [
                    ClockAction.NORMAL_CLOCK_IN,
                    ClockAction.EARLY_CLOCK_IN,
                    ClockAction.LATE_CLOCK_IN,
                    ClockAction.EARLY_LUNCH_CLOCK_IN,
                    ClockAction.LATE_LUNCH_CLOCK_IN,
                    ClockAction.NORMAL_LUNCH_CLOCK_IN
                ].includes(type)
            );
        return isClockingIn ? "Clock in" : "Clock out";
    },
    [`${ActiveEntityType.PRODUCT}.attributes`]: (product: ActiveProduct) => {
        if (product?.attributes && Array.isArray(product.attributes)) {
            const concatenatedValues = product.attributes
                .filter(attr => !attr.isDeleted && attr.values && attr.values.length > 0)
                .map(attr => attr.values.join(", "))
                .join(", ");
            return concatenatedValues || undefined;
        }
        let attsFromMeta = [];
        // line items have metadata as an array of objects
        // it's bad mapping but must solve for now
        // @ts-ignore
        if (!product?.metadata) return undefined;
        // @ts-ignore
        for (const m of product?.metadata) {
            if (!m.key?.startsWith("_")) {
                attsFromMeta.push(m.value);
            }
        }
        return attsFromMeta.join(", ");
    },
    // [`${ActiveEntityType.PRODUCT}.fee`]: (product: ActiveProduct) => {
    //     if (product?.fee) {
    //         // again weird line-item format...
    //         // @ts-ignore
    //         return product?.fee?.amount || product?.fee?.percentage || 0;
    //     }
    //     return undefined;
    // },
    [`${ActiveEntityType.PRODUCT}.itemDiscount`]: (lineItem: LineItem) => {
        return (
            Number(lineItem?.discount?.itemDiscount?.amount || "0") +
            (lineItem?.discount?.cartDiscount ? Number(lineItem?.discount?.cartDiscount?.amount) / lineItem?.quantity : 0)
        );
    },
    [`${ActiveEntityType.ORDER}.cartFees`]: (order: ActiveOrder, formatCurrency: (amount: number) => string | number) => {
        // return order?.cartFees?.map((fee: any) => fee?.amount || fee?.percentage || 0).join(", ") || 0;
        const total = (order?.cartFees || []).reduce((acc: number, fee: any) => {
            let amount = 0;
            if (fee?.amount || fee?.amount === 0) {
                if (typeof fee?.amount === "string") {
                    amount = parseFloat(fee?.amount);
                } else {
                    amount = fee?.amount;
                }
            }
            return acc + amount;
        }, 0);
        return formatCurrency(total);
    },
    [`${ActiveEntityType.ORDER}.totalFees`]: (order: ActiveOrder, formatCurrency: (amount: number) => string | number) => {
        // return order?.cartFees?.map((fee: any) => fee?.amount || fee?.percentage || 0).join(", ") || 0;
        let total = 0;
        order.cartFees?.forEach((fee: any) => {
            if (fee?.amount || fee?.amount === 0) {
                if (typeof fee?.amount === "string") {
                    total += parseFloat(fee?.amount);
                } else {
                    total += fee?.amount;
                }
            }
        });
        order.lineItems?.forEach((item: any) => {
            if (item.fee?.itemFee?.amount || item.fee?.itemFee?.amount === 0) {
                if (typeof item.fee.itemFee.amount === "string") {
                    total += parseFloat(item.fee.itemFee.amount);
                } else {
                    total += item.fee.itemFee.amount;
                }
            }
        });

        return formatCurrency(total);
    },
    [`${ActiveEntityType.ORDER}.payments`]: (order: ActiveOrder, formatCurrency: (amount: number) => string | number) => {
        let paymentMethodsToUse;

        if ((order as unknown as RefundItem).refundPayment) {
            paymentMethodsToUse = (order as unknown as RefundItem).refundPayment;
        } else if (order?.paymentMethods) {
            paymentMethodsToUse = order?.paymentMethods;
        }
        let combinedStr = "";
        if (
            paymentMethodsToUse &&
            Array.isArray(paymentMethodsToUse) &&
            paymentMethodsToUse?.some(payment => payment?.paymentType)
        ) {
            for (let i = paymentMethodsToUse.length - 1; i >= 0; i--) {
                const payment = paymentMethodsToUse[i];
                const paymentTypeName = payment?.paymentType || "payment";
                const formattedAmount = payment?.amount != null ? formatCurrency(Number(payment.amount)) : "";
                combinedStr += `${paymentTypeName}: ${formattedAmount} `;
                if (payment.tip?.amount) {
                    combinedStr += `(${formatCurrency(Number(payment.tip.amount))} tip)`;
                }
                if (i > 0) combinedStr += ", ";
            }
        }
        return combinedStr;
    }
};

export const processWebhookOutput = (
    webhookUrl: string,
    fields: DynamicField[],
    payloadType: string,
    t: (v: string) => string,
    fromBuilder: boolean,
    data: any
) => {
    if (!webhookUrl) {
        return "";
    }

    if (payloadType === "JSON") {
        return webhookUrl.trim();
    }

    let url;
    try {
        url = new URL(webhookUrl.trim());
    } catch (e) {
        console.error("Invalid webhook URL:", e);
        return webhookUrl.trim();
    }

    const processedFields = fields
        .filter(field => field && field.name)
        .map(field => {
            const fieldValue =
                (field.type as string) === "Custom entry"
                    ? field.value
                    : processDynamicData({
                          fromBuilder,
                          dynamicDataString: field.dynamicData || "",
                          dataContext: field.type!,
                          allData: data,
                          t
                      });

            const encodedName = encodeURIComponent(field.name.trim());

            if (typeof fieldValue === "string") {
                return `${encodedName}=${encodeURIComponent(fieldValue.trim())}`;
            }

            if (fromBuilder) {
                const dynamicValue = fieldValue.dynamicData || "";
                return `${encodedName}=[${dynamicValue}]`;
            }

            const dynamicValue = fieldValue.dynamicData;
            const stringValue = dynamicValue != null ? String(dynamicValue) : "";
            return `${encodedName}=${encodeURIComponent(stringValue.trim())}`;
        })
        .filter(Boolean);

    if (processedFields.length > 0) {
        const searchParams = url.searchParams;

        processedFields.forEach(field => {
            const [key, value] = field.split("=");
            searchParams.append(decodeURIComponent(key), decodeURIComponent(value));
        });

        url.search = searchParams.toString();
    }

    return url.toString();
};
